import { Dispatch, SetStateAction, useState } from 'react';
import { AuthLayout } from '../../../components/layouts';
import { OtpVerification, Stepper } from '../../../components/ui';
import { toast } from 'react-toastify';
import AuthService from '../../../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUserInfo } from '../../../redux/reducers/authSlice';
import {
    PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY,
    PARENT_POST_SIGNUP_ENROLLMENT_KEY,
} from '../../../constants/sessionStorageKeys';
import { ShieldCheck } from 'lucide-react';

interface Props {
    setSubmitForm: Dispatch<SetStateAction<boolean>>;
    formData: any;
    referralId: any;
}

export default function OtpVerificationForm({ formData, referralId }: Props) {
    const authService = new AuthService();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);

    const isEmailAlreadyUsedMessage = (message: string = '') =>
        message.toLowerCase().includes('email already used') ||
        message.toLowerCase().includes('already exists');

    const obj: any = localStorage.getItem('email-confirmation');
    const tdx = obj ? JSON.parse(obj) : null;

    const handleVerification = async (data: number | string) => {
        const newOtp = tdx?.data?.otp + '1';
        if (data === newOtp) {
            registerParent();
        } else {
            toast.error('Invalid OTP!');
        }
    };

    const handleOtpResend = async () => {
        setOtpLoading(true);
        try {
            const response = await authService.verifyEmail({ email: formData.email });
            setOtpLoading(false);
            if (!response.status) {
                toast.error(response?.message);
                return;
            }
            toast.success(response?.message);
            localStorage.setItem('email-confirmation', JSON.stringify(response));
        } catch (err: any) {
            setOtpLoading(false);
            toast.error(err?.message);
        }
    };

    const registerParent = async () => {
        setLoading(true);
        try {
            const { preferredClass, ...signUpPayload } = formData || {};
            const response = await authService.signUp(signUpPayload);
            setLoading(false);
            if (!response.status) {
                if (isEmailAlreadyUsedMessage(response?.message)) {
                    toast.error('This email already has an account. Please sign in or reset password.');
                    navigate('/parent/sign-in');
                    return;
                }
                toast.error(response?.message);
                return;
            }

            if (referralId) {
                sessionStorage.setItem('referralId', referralId);
            }

            localStorage.setItem('ryd-parent-token', response?.data?.token);
            try {
                const preferred = String(preferredClass || '').toLowerCase();
                if (preferred === 'mathematics' || preferred === 'other') {
                    sessionStorage.setItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY, '1');
                    sessionStorage.setItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY, preferred);
                } else {
                    sessionStorage.removeItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY);
                    sessionStorage.setItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY, 'coding');
                }
            } catch {
                /* ignore */
            }
            dispatch(setUserInfo(response.data));
            navigate('/success');
        } catch (err: any) {
            setLoading(false);
            toast.error(err.message);
        }
    };

    return (
        <AuthLayout>
            <Stepper currentTab={3} />

            <div className="mb-5 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-ryd-primaryLess1 text-ryd-primary">
                    <ShieldCheck size={22} />
                </div>
                <h1 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[26px]">
                    Verify your email
                </h1>
                <p className="mt-1.5 px-1 text-[13px] text-[#8b9aab] sm:text-[14px]">
                    Enter the code we sent to finish creating your account.
                </p>
            </div>

            {tdx?.message ? (
                <p className="mb-4 rounded-[12px] bg-emerald-50 px-3 py-2 text-center text-[13px] text-emerald-700">
                    {tdx.message}
                </p>
            ) : null}

            <div className="mx-auto">
                <OtpVerification
                    btnText={loading ? 'Processing...' : 'Verify OTP'}
                    handleVerification={handleVerification}
                />
            </div>

            <p className="mt-4 text-center text-[13px] text-[#8b9aab]">
                Didn&apos;t receive code?{' '}
                <button
                    type="button"
                    className="font-semibold text-ryd-primary hover:underline"
                    onClick={handleOtpResend}
                >
                    {otpLoading ? 'Resending…' : 'Resend'}
                </button>
            </p>
        </AuthLayout>
    );
}
